// PrivacyPolicy.jsx
import React, { useEffect } from "react";
import "./PrivacyPolicy.css";
import Footer from "./Footer";
import Navbar from "./Navbar";

const PrivacyPolicy = () => {
  const currentYear = new Date().getFullYear();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <>
      <Navbar />
      <section className="privacy-policy" id="privacy-policy">
        <div className="privacy-container">
          {/* Header */}
          <div className="privacy-header">
            <span className="privacy-tag">Legal</span>
            <h1 className="privacy-title">
              Privacy <span>Policy</span>
            </h1>
            <p className="privacy-subtitle">
              Your privacy matters to us. Read how we collect, use, and protect 
              your personal information when you use our services.
            </p>
            <div className="privacy-meta">
              <span className="privacy-updated">
                <i className="fas fa-calendar-alt"></i> Last Updated: {currentYear}
              </span>
              <span className="privacy-version">Version 1.0</span>
            </div>
          </div>

          {/* Content */}
          <div className="privacy-content">
            <div className="privacy-sidebar">
              <div className="privacy-toc">
                <h3>Table of Contents</h3>
                <ul>
                  <li><a href="#section-1">1. Introduction</a></li>
                  <li><a href="#section-2">2. Information We Collect</a></li>
                  <li><a href="#section-3">3. How We Use Your Information</a></li>
                  <li><a href="#section-4">4. Information Sharing</a></li>
                  <li><a href="#section-5">5. Data Security</a></li>
                  <li><a href="#section-6">6. Your Rights</a></li>
                  <li><a href="#section-7">7. Cookies</a></li>
                  <li><a href="#section-8">8. Third-Party Services</a></li>
                  <li><a href="#section-9">9. Children's Privacy</a></li>
                  <li><a href="#section-10">10. Changes to Policy</a></li>
                  <li><a href="#section-11">11. Contact Us</a></li>
                </ul>
              </div>
            </div>

            <div className="privacy-main">
              {/* Section 1 */}
              <div className="privacy-section" id="section-1">
                <h2>1. Introduction</h2>
                <p>
                  Welcome to Atoz Events. We respect your privacy and are committed to 
                  protecting your personal information. This Privacy Policy explains how 
                  we collect, use, disclose, and safeguard your information when you visit 
                  our website or use our decoration services.
                </p>
                <p>
                  By using our website and services, you agree to the collection and use 
                  of information in accordance with this policy. If you do not agree with 
                  any part of this policy, please do not use our services.
                </p>
              </div>

              {/* Section 2 */}
              <div className="privacy-section" id="section-2">
                <h2>2. Information We Collect</h2>
                <h3>Personal Information</h3>
                <p>We collect information that you voluntarily provide to us when you:</p>
                <ul>
                  <li>Book our decoration services</li>
                  <li>Contact us through our website or phone</li>
                  <li>Fill out forms on our website</li>
                  <li>Communicate with us via email, WhatsApp, or phone</li>
                </ul>
                <p>This may include:</p>
                <ul>
                  <li><strong>Name</strong> - Your full name</li>
                  <li><strong>Contact Information</strong> - Phone number, email address</li>
                  <li><strong>Event Details</strong> - Event type, date, venue, requirements</li>
                  <li><strong>Payment Information</strong> - Transaction details (we do not store payment card information directly)</li>
                  <li><strong>Communication Data</strong> - Messages, inquiries, and feedback</li>
                </ul>

                <h3>Automatically Collected Information</h3>
                <p>When you visit our website, we may automatically collect:</p>
                <ul>
                  <li><strong>Device Information</strong> - IP address, browser type, device type</li>
                  <li><strong>Usage Data</strong> - Pages visited, time spent, click patterns</li>
                  <li><strong>Location Information</strong> - General location based on IP address</li>
                </ul>
              </div>

              {/* Section 3 */}
              <div className="privacy-section" id="section-3">
                <h2>3. How We Use Your Information</h2>
                <p>We use your information for the following purposes:</p>
                <ul>
                  <li><strong>To Provide Services</strong> - Process bookings, deliver decoration services, and manage your events</li>
                  <li><strong>To Communicate</strong> - Respond to inquiries, send booking confirmations, and provide updates</li>
                  <li><strong>To Improve Services</strong> - Analyze usage patterns and customer feedback to enhance our offerings</li>
                  <li><strong>To Personalize Experience</strong> - Tailor our services to your preferences and event needs</li>
                  <li><strong>To Send Promotional Content</strong> - Share information about our services, offers, and events (with your consent)</li>
                  <li><strong>To Ensure Security</strong> - Monitor and protect our website against fraud and unauthorized access</li>
                  <li><strong>To Comply with Legal Obligations</strong> - Fulfill legal and regulatory requirements</li>
                </ul>
              </div>

              {/* Section 4 */}
              <div className="privacy-section" id="section-4">
                <h2>4. Information Sharing</h2>
                <p>
                  We value your trust and do not sell, trade, or rent your personal information 
                  to third parties. However, we may share your information in the following situations:
                </p>
                <ul>
                  <li><strong>With Service Providers</strong> - Third-party vendors who assist us in delivering our services (e.g., payment processors, delivery partners)</li>
                  <li><strong>With Your Consent</strong> - When you give us explicit permission to share your information</li>
                  <li><strong>For Legal Reasons</strong> - When required by law, court order, or government regulation</li>
                  <li><strong>Business Transfers</strong> - In the event of a merger, acquisition, or sale of assets</li>
                </ul>
                <p>
                  We ensure that any third-party service providers are bound by data protection 
                  obligations and use your information only for the specified purposes.
                </p>
              </div>

              {/* Section 5 */}
              <div className="privacy-section" id="section-5">
                <h2>5. Data Security</h2>
                <p>
                  We implement appropriate technical and organizational measures to protect your 
                  personal information against unauthorized access, disclosure, alteration, or destruction.
                </p>
                <ul>
                  <li><strong>Encryption</strong> - We use SSL/TLS encryption for data transmission</li>
                  <li><strong>Access Control</strong> - Limited access to your information to authorized personnel only</li>
                  <li><strong>Secure Storage</strong> - Your data is stored on secure servers with industry-standard protection</li>
                  <li><strong>Regular Audits</strong> - We conduct periodic security assessments to identify and address vulnerabilities</li>
                </ul>
                <p>
                  While we strive to protect your information, no method of transmission over the 
                  internet is 100% secure. We cannot guarantee absolute security but are committed 
                  to safeguarding your privacy.
                </p>
              </div>

              {/* Section 6 */}
              <div className="privacy-section" id="section-6">
                <h2>6. Your Rights</h2>
                <p>You have the following rights regarding your personal information:</p>
                <ul>
                  <li><strong>Right to Access</strong> - Request a copy of the personal information we hold about you</li>
                  <li><strong>Right to Rectification</strong> - Request correction of inaccurate or incomplete information</li>
                  <li><strong>Right to Erasure</strong> - Request deletion of your personal information (subject to legal obligations)</li>
                  <li><strong>Right to Restrict Processing</strong> - Request limitation of how we use your data</li>
                  <li><strong>Right to Data Portability</strong> - Request transfer of your data to another service provider</li>
                  <li><strong>Right to Withdraw Consent</strong> - Withdraw consent at any time for processing based on consent</li>
                  <li><strong>Right to Object</strong> - Object to processing for direct marketing purposes</li>
                </ul>
                <p>
                  To exercise any of these rights, please contact us using the details provided 
                  in the "Contact Us" section below.
                </p>
              </div>

              {/* Section 7 */}
              <div className="privacy-section" id="section-7">
                <h2>7. Cookies</h2>
                <p>
                  We use cookies and similar tracking technologies to enhance your browsing 
                  experience, analyze site traffic, and personalize content.
                </p>
                <h3>Types of Cookies We Use:</h3>
                <ul>
                  <li><strong>Essential Cookies</strong> - Necessary for website functionality and security</li>
                  <li><strong>Preference Cookies</strong> - Remember your preferences and settings</li>
                  <li><strong>Analytics Cookies</strong> - Help us understand how visitors interact with our website</li>
                  <li><strong>Marketing Cookies</strong> - Used to deliver relevant advertisements</li>
                </ul>
                <p>
                  You can control cookie preferences through your browser settings. However, 
                  disabling certain cookies may affect your experience on our website.
                </p>
              </div>

              {/* Section 8 */}
              <div className="privacy-section" id="section-8">
                <h2>8. Third-Party Services</h2>
                <p>
                  Our website may contain links to third-party websites and services. We are 
                  not responsible for the privacy practices or content of these external sites.
                </p>
                <p>
                  Third-party services we may use include:
                </p>
                <ul>
                  <li>Payment gateways for processing transactions</li>
                  <li>Communication platforms (WhatsApp, email services)</li>
                  <li>Analytics services (Google Analytics)</li>
                  <li>Social media platforms (Facebook, Instagram, YouTube)</li>
                </ul>
                <p>
                  We encourage you to review the privacy policies of any third-party services 
                  you interact with.
                </p>
              </div>

              {/* Section 9 */}
              <div className="privacy-section" id="section-9">
                <h2>9. Children's Privacy</h2>
                <p>
                  Our services are not intended for individuals under the age of 18. We do 
                  not knowingly collect personal information from children. If you are a parent 
                  or guardian and believe your child has provided us with personal information, 
                  please contact us immediately.
                </p>
                <p>
                  If we discover that we have collected personal information from a child 
                  without parental consent, we will take steps to delete that information promptly.
                </p>
              </div>

              {/* Section 10 */}
              <div className="privacy-section" id="section-10">
                <h2>10. Changes to This Policy</h2>
                <p>
                  We may update this Privacy Policy from time to time to reflect changes in 
                  our practices, legal requirements, or operational needs. We will notify you 
                  of any significant changes by:
                </p>
                <ul>
                  <li>Posting the updated policy on our website</li>
                  <li>Updating the "Last Updated" date at the top of this page</li>
                  <li>Notifying you via email or WhatsApp (if you have provided contact details)</li>
                </ul>
                <p>
                  We encourage you to review this policy periodically to stay informed about 
                  how we protect your information.
                </p>
              </div>

              {/* Section 11 */}
              <div className="privacy-section" id="section-11">
                <h2>11. Contact Us</h2>
                <p>
                  If you have any questions, concerns, or requests regarding this Privacy Policy 
                  or your personal information, please contact us:
                </p>
                <div className="privacy-contact-details">
                  <div className="privacy-contact-item">
                    <i className="fas fa-phone-alt"></i>
                    <div>
                      <strong>Phone:</strong> <a href="tel:7023332099">7023332099</a>
                    </div>
                  </div>
                  <div className="privacy-contact-item">
                    <i className="fab fa-whatsapp"></i>
                    <div>
                      <strong>WhatsApp:</strong> <a href="https://wa.me/917023332099">7023332099</a>
                    </div>
                  </div>
                  <div className="privacy-contact-item">
                    <i className="fas fa-envelope"></i>
                    <div>
                      <strong>Email:</strong> <a href="mailto:info@atozevents.in">info@atozevents.in</a>
                    </div>
                  </div>
                  <div className="privacy-contact-item">
                    <i className="fas fa-map-marker-alt"></i>
                    <div>
                      <strong>Address:</strong> 720/5, Patel Nagar, Gurugram, Haryana 122001
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Note */}
          <div className="privacy-footer-note">
            <p>
              © {currentYear} <span>Atoz Events</span>. All Rights Reserved. 
              Protecting your privacy is our priority.
            </p>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default PrivacyPolicy;